/**
 * Shared "split N participants into balanced groups" logic — used by any
 * format that needs a grouping/regions step (group_knockout, regional_bracket,
 * and future ones). Kept separate from groupKnockoutGenerator.js since it's
 * no longer specific to that one format.
 */

/**
 * Auto-suggests a group count aiming for ~4 participants per group, the
 * common sweet spot for pool play / a region. Exposed separately so the UI's
 * grouping step can show/prefill this default and let the organizer nudge it.
 */
export const suggestGroupCount = (n) => {
  const targetGroupSize = 4;
  const suggested = Math.round(n / targetGroupSize);
  const maxPossible = Math.floor(n / 2); // every group needs >= 2 to hold a match
  return Math.max(2, Math.min(suggested, maxPossible));
};

/**
 * Serpentine (snake) seeding so group/region strength stays balanced by seed:
 * A,B,C,D,D,C,B,A,A,B,C,D... instead of just chunking in seed order.
 */
export const assignGroups = (participants, groupCount) => {
  const groups = Array.from({ length: groupCount }, () => []);
  participants.forEach((p, i) => {
    const cycle = Math.floor(i / groupCount);
    const posInCycle = i % groupCount;
    const groupIndex = cycle % 2 === 0 ? posInCycle : groupCount - 1 - posInCycle;
    groups[groupIndex].push(p);
  });
  return groups;
};

/**
 * Fisher-Yates shuffle, used by the "Randomize" action in the group
 * assignment editor — shuffle the participant order first, then run the same
 * assignGroups split on the shuffled order, so the result is still a
 * balanced group-count split, just no longer seed-ordered.
 */
export const shuffleParticipants = (participants) => {
  const arr = [...participants];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};
